import { useEffect, useState } from "react";
import * as Sentry from "@sentry/react-native";
import { StreamChat } from "stream-chat";

import EnvironmentVariablesLoader from "../../utils/environment";
import { useSession } from "../auth/useSession";

import { checkNetworkConnection } from "@/utils/network";
import { showNetworkErrorAlert } from "@/utils/alerts";

const environmentVariables =
  EnvironmentVariablesLoader.getEnvironmentVariables();

type ChatClientInfo = {
  chatClient: StreamChat | null;
  clientIsReady: boolean;
  unreadCount: number;
};

export const useChatClient = (): ChatClientInfo => {
  const { session, signOut } = useSession();
  const [chatClient, setChatClient] = useState<StreamChat | null>(null);
  const [clientIsReady, setClientIsReady] = useState(false);
  const [unreadCount, setUnreadCount] = useState<number>(0);

  useEffect(() => {
    const setupClient = async (): Promise<void> => {
      try {
        if (session !== null && session !== undefined) {
          const chatClient = StreamChat.getInstance(
            environmentVariables.stream.chatApiKey,
          );

          const userStreamInfo = {
            id: session.identity.username,
            name: session.identity.username,
          };

          while (
            chatClient.userID !== null &&
            chatClient.userID !== undefined
          ) {
            await chatClient.disconnectUser();
          }

          const user = await chatClient.connectUser(
            userStreamInfo,
            session.identity.streamToken,
          );

          const initialUnreadCount = user?.me?.total_unread_count;
          if (initialUnreadCount !== undefined) {
            setUnreadCount(initialUnreadCount);
          }

          setChatClient(chatClient);
          setClientIsReady(true);
        }
      } catch (error) {
        if (error instanceof Error) {
          const networkStatus = await checkNetworkConnection();
          if (
            networkStatus.isConnected === false ||
            networkStatus.isInternetReachable === false
          ) {
            showNetworkErrorAlert(setupClient, session);
          } else {
            Sentry.captureException(error);
            showNetworkErrorAlert(setupClient, session, signOut);
          }
        }
      }
    };

    // If the chat client has a value in the field `userID`, a user is already connected
    // and we can skip trying to connect the user again.
    if (chatClient === undefined || chatClient === null || !chatClient.userID) {
      setupClient();
    }
  }, []);

  /**
   * Listen to changes in unread counts and update the badge count
   */
  useEffect(() => {
    const listener = chatClient?.on((e: { total_unread_count?: number }) => {
      if (e.total_unread_count !== undefined) {
        setUnreadCount(e.total_unread_count);
      } else {
        const countUnread = Object.values(chatClient.activeChannels).reduce(
          (count, channel: { countUnread?: () => number }) =>
            count + (channel?.countUnread?.() || 0),
          0,
        );
        setUnreadCount(countUnread);
      }
    });
    return () => {
      if (listener) {
        listener.unsubscribe();
      }
    };
  }, [chatClient]);

  return {
    chatClient,
    clientIsReady,
    unreadCount,
  };
};
